import { Controller } from '@hotwired/stimulus'

const TYPING_PAUSE = 400

export default class AutoFilterController extends Controller<HTMLElement> {
  static targets = ['form', 'table']

  declare readonly formTarget: HTMLFormElement
  declare readonly tableTarget: HTMLElement
  declare readonly hasFormTarget: boolean
  declare readonly hasTableTarget: boolean

  private timer: number | undefined
  private page = 1

  private readonly onChange = (): void => {
    this.page = 1
    this.apply()
  }

  private readonly onInput = (event: Event): void => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type === 'date') {
      return
    }

    window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => {
      this.page = 1
      this.apply()
    }, TYPING_PAUSE)
  }

  private readonly onPaginate = (event: Event): void => {
    this.page = (event as CustomEvent<{ page: number }>).detail.page
    this.apply()
  }

  connect (): void {
    // The filters listen to their own form, never the whole card: anything else
    // inside it, a modal above a row for instance, would otherwise refilter the
    // list and swap the table out from under whoever was using it.
    this.formTarget.addEventListener('change', this.onChange)
    this.formTarget.addEventListener('input', this.onInput)
    this.element.addEventListener('pagination:paginate', this.onPaginate)
  }

  disconnect (): void {
    window.clearTimeout(this.timer)
    this.formTarget.removeEventListener('change', this.onChange)
    this.formTarget.removeEventListener('input', this.onInput)
    this.element.removeEventListener('pagination:paginate', this.onPaginate)
  }

  private apply (): void {
    window.clearTimeout(this.timer)

    if (!this.hasTableTarget || !this.hasFormTarget) {
      this.formTarget.submit()
      return
    }

    const query = new URLSearchParams(new FormData(this.formTarget) as unknown as Record<string, string>)

    if (this.page > 1) {
      query.set('page', String(this.page))
    }

    const url = `${this.formTarget.action}?${query.toString()}`

    this.tableTarget.style.opacity = '0.4'

    fetch(`${url}&partial=table`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(async (response) => await response.text())
      .then((html) => {
        this.tableTarget.innerHTML = html
        this.tableTarget.style.opacity = '1'
        window.history.replaceState({}, '', url)
      })
      .catch(() => {
        // The page still answers the same URL in full, so fall back to loading it.
        window.location.assign(url)
      })
  }
}
